"use client";

import { FileUser } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";

import { TenantLogo } from "@/components/tenant-logo";
import { tenantLogoFor } from "@/config/tenant-assets";
import type { TenantIdentity } from "@/config/tenants";
import { currentUser } from "@/features/auth/services/auth-service";
import { useAppLocale } from "@/features/localization/app-locale-provider";

export function LegalPageHeader({ tenant }: Readonly<{ tenant: TenantIdentity }>) {
  const { locale, setLocale } = useAppLocale();
  const [destination, setDestination] = useState(`/${tenant.code}/dashboard`);
  const logo = tenantLogoFor(tenant.code, locale);

  useEffect(() => {
    let active = true;

    void currentUser()
      .then(() => {
        if (active) setDestination(`/${tenant.code}/dashboard`);
      })
      .catch(() => {
        if (active) setDestination(`/${tenant.code}/login`);
      });

    return () => {
      active = false;
    };
  }, [tenant.code]);

  return (
    <header className="flex flex-wrap items-center justify-between gap-4 border-b border-border pb-6">
      <Link
        aria-label={`${tenant.displayName} home`}
        className="inline-flex items-center rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        href={destination}
      >
        {logo ? (
          <TenantLogo
            className="size-16 rounded-xl object-cover sm:size-20"
            locale={locale}
            priority
            tenant={tenant.code}
          />
        ) : (
          <span
            aria-hidden="true"
            className="grid size-14 place-items-center rounded-xl bg-primary text-primary-foreground shadow-sm sm:size-16"
          >
            <FileUser className="size-7" />
          </span>
        )}
      </Link>

      <nav
        className="flex items-center gap-2 text-sm font-semibold"
        aria-label={locale === "fr" ? "Langue" : "Language"}
      >
        <button
          type="button"
          onClick={() => setLocale("en")}
          className={locale === "en" ? "text-primary" : "text-muted-foreground"}
          aria-pressed={locale === "en"}
        >
          EN
        </button>
        <span className="text-muted-foreground">/</span>
        <button
          type="button"
          onClick={() => setLocale("fr")}
          className={locale === "fr" ? "text-primary" : "text-muted-foreground"}
          aria-pressed={locale === "fr"}
        >
          FR
        </button>
      </nav>
    </header>
  );
}
